<template>
  <div id="floorplan-detail">
    <MoleculesLoadingOverlay v-if="loading" />
    <div v-else>
      <OrganismsProductDetailBW :organism="brandWindow" />
      <OrganismsFlexibleFeatures
        v-if="flexibleFeatures.item"
        :organism="flexibleFeatures"
      />
      <OrganismsTabbedContent
        v-if="tabbedContent.defaultTabHash && !hasIframe"
        :organism="tabbedContent"
      >
        <template v-for="item in tabbedContent.item" :key="item.id" #[item.id]>
          <img
            class="mx-auto mb-5"
            :src="item.image.src"
            :alt="item.image.alt"
          />
          <MoleculesButtons
            :buttons="tabbedContent.button"
            :button-colors="tabbedContent.button.colors"
            class="!justify-center mx-auto"
          />
        </template>
      </OrganismsTabbedContent>
      <OrganismsSimpleIframe v-if="hasIframe" :organism="simpleIframe" />
      <OrganismsGalleryGrid
        id="featured-photos-gallery"
        v-if="galleryGrid"
        :organism="galleryGrid"
      />
      <OrganismsVirtualTour
        id="virtual-tour"
        v-if="virtualTour.virtualTourUrl"
        class="mx-auto my-24 bg-gray-900"
        :organism="virtualTour"
      />
      <OrganismsTextCTA
        id="cta-be-the-first"
        :organism="beTheFirst"
        class="my-24"
      />
      <OrganismsModalForm>
        <header class="mb-4">
          <h2 class="mb-4 text-4xl font-semibold text-black font-figtree">
            Get More Info About This Floorplan
          </h2>
        </header>
        <FormsDynamicGravityForm :gfid="3" />
      </OrganismsModalForm>
      <OrganismsPopularPlans
        v-if="popularPlans.items.length"
        :organism="popularPlans"
      />
      <div class="block h-24 bg-gradient-to-b from-gray to-white"></div>
    </div>

    <OrganismsModalLogin />
  </div>
</template>

<style>
#floorplan-detail {
  .text-cta {
    p {
      @apply leading-8;

      a {
        @apply text-sm text-primary-800 underline;
      }
    }

    .buttons a {
      @apply uppercase;
    }
  }

  .virtual-tour {
    h2 {
      @apply text-white;
    }

    .description {
      @apply hidden;
    }
  }

  .tabbed-content {
    header {
      background-image: linear-gradient(101deg, #2d3142ff 0%, #4f5d75ff 97%);
      .header-content {
        @apply max-w-[556px];
      }
    }
    .tabs-component-tabs {
      @apply mx-auto flex-col md:flex-row justify-center;

      .tabs-component-tab {
        @apply md:max-w-[210px];

        .tabs-component-tab-a {
          @apply whitespace-nowrap;
        }
      }
    }
    .tabs-component-panels {
      .buttons a {
        @apply text-[15px] uppercase font-figtree font-medium py-3 px-10;
      }
    }
  }
  .popular-plans {
    .product-grid {
      @apply w-full;

      .price {
        @apply hidden p-0 top-0;
      }
    }
  }

  .swiper-pagination {
    @apply bottom-12;
  }

  .product-detail-brand-window {
    .buttons {
      @apply flex-wrap;

      .button-2-wrapper {
        @apply sm:ml-0 sm:mt-4;
      }
    }

    
  }
}
</style>

<script setup>
const route = useRoute();
//import { useQuery } from "@vue/apollo-composable";
import { useAsyncQuery } from "#imports";
import { GET_PLAN } from "~/graphql/GetPlan";
const { data, pending, error } = await useAsyncQuery(GET_PLAN, { uri: route.path }, { fetchPolicy: "no-cache" });
import { storeToRefs } from "pinia";
const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
const modalForm = useModalForm();
const isFormSubmitted = ref(false);

const plan = computed(() => {
  return data?.value?.plan ?? [];
});

const plans = computed(() => {
  return data?.value?.plans?.nodes ?? [];
});

const isSmallScreen = ref(false);

const collectionFeatures = computed(() => {
  let parsedJSON = {};
  try {
    parsedJSON = JSON.parse(data?.value?.collectionFeatures);
  } catch (e) {
    // console.log("Invalid collectionFeatures JSON:", e);
    return {};
  }
  return parsedJSON;
});

const loading = pending;

const computedBreadcrumbs = computed(() => {
  const neighborhoodBreadcrumbs =
    plan.value?.floorplanDetails?.neighborhood?.map((neighborhood) => ({
      title: neighborhood.title + ", " + neighborhood.neighborhood.city,
      link: neighborhood.uri,
    })) || [];

  /*return [
    ...neighborhoodBreadcrumbs,
    {
      title: "Floorplans",
      link: plan.value?.floorplanDetails?.neighborhood[0]?.uri + "#floorplans",
    },
    {
      title: plan.value?.title + " Plan",
    },
  ];*/
  return [
    { title: "Floorplans", link: "/our-homes/" },
    {
      title: plan.value?.title + " Plan",
    },
  ];
});

watch(computedBreadcrumbs, () => {
  breadcrumbsList.value = computedBreadcrumbs.value;
});

const brandWindow = computed(() => {
  //const isHeritage = plan?.value?.floorplanDetails?.collection === 'heritage';
  return {
    collection: plan?.value?.floorplanDetails?.collection,
    title: plan?.value?.title,
    // title: plan?.value?.title,
    type: plan?.value?.floorplanDetails?.type,
    addType: plan?.value?.floorplanDetails?.floorplanAddType,
    neighborhoods: plan?.value?.floorplanDetails?.neighborhood?.map(
      (neighborhood) => ({
        neighborhoodTitle: neighborhood.title,
        neighborhoodLink: neighborhood.uri,
        neighborhoodHide: neighborhood.neighborhood.hideCommunity, // Assuming you want to use the slug as the link
      }),
    ),
    bedsMin: plan?.value?.floorplanDetails?.bedroomsMin,
    bedsMax: plan?.value?.floorplanDetails?.bedroomsMax,
    bathsMin: plan?.value?.floorplanDetails?.bathroomsMin,
    bathsMax: plan?.value?.floorplanDetails?.bathroomsMax,
    garageMin: plan?.value?.floorplanDetails?.garageMin,
    garageMax: plan?.value?.floorplanDetails?.garageMax,
    sqftMin: plan?.value?.floorplanDetails?.mainSqrFt,
    sqftMax: plan?.value?.floorplanDetails?.finishedSqrFt,
    priceMin: plan?.value?.floorplanDetails?.basePrice,
    planPrice: plan?.value?.floorplanDetails?.planPriceByCommunity,
    autoplaySlider: true,
    autoplayDelay: 10000,
    sliderImages: plan?.value?.floorplanDetails?.elevations,
    button1: true,
    button1Text: "Get Brochure",
    button1Target: isSmallScreen.value ? "_self" : "_blank",
    link1: plan?.value?.floorplanDetails?.pdfEPub ?? false,
    button2: true,
    button2Text: "Customize this floorplan",
    button2Target: "_blank",
    button2Color: "button-blue",
    link2: plan?.value?.floorplanDetails?.eciLink ?? false,
    hasFavorites: true,
    id: plan?.value?.databaseId,
    thumbnail: getImageSrc(
      plan?.value?.floorplanDetails?.elevations[0],
      "medium",
    ),
  };
});

const simpleIframe = computed(() => {
  return {
    title1: "Customize this floorplan",
    title2: plan?.value?.floorplanDetails?.displayTitle,
    iframe: plan?.value?.floorplanDetails?.eciIframe,
  };
});

const hasIframe = computed(() => simpleIframe.value && simpleIframe.value.iframe);

const tabbedContent = computed(() => {
  const tabs = Array.isArray(plan?.value?.floorplanDetails?.tabs)
    ? plan.value.floorplanDetails.tabs.map((tab, currentIndex, array) => {
        let tabName = tab.title;
        let titleCount = 0;

        array.forEach(function (checkTab, checkIndex, checkArray) {
          if (tab.title === checkTab.title) {
            titleCount++;

            if (currentIndex > checkIndex) {
              tabName = `${tab.title} - Opt ${titleCount}`;
            }
          }
        });

        return {
          name: tabName,
          id: kebabCase(tabName),
          image: {
            src: getImageSrc(tab.image, "large"),
            alt: tab?.image?.altText,
          },
        };
      })
    : [];

  return {
    containerWidth: "default",
    title1: "Explore this floorplan",
    title2: plan?.value?.title,
    item: tabs,
    defaultTabHash: tabs[0]?.id,
    variant: "product-detail",
    button: {
      colors: ["button-yellow"],
      button1: true,
      button1Text: "Get Brochure",
      button1Target: "_blank",
      link1: plan?.value?.floorplanDetails?.pdfEPub,
    },
  };
});

const beTheFirst = computed(() => {
  return {
    title1: "Become a VIP",
    description: `<p>Join our VIP List and be first in line to hear about new Consort Homes neighborhoods, new home plans, and events.</p>`,
    button1: true,
    button1Text: "Get Updates",
  };
});

const flexibleFeatures = computed(() => {
  const collection = plan?.value?.floorplanDetails?.collection;
  let collectionTitle = "";
  let featuresList = [];

  switch (true) {
    case collection?.toLowerCase().includes("heritage"):
      collectionTitle = "Heritage";
      featuresList = collectionFeatures?.value?.heritage ?? [];
      break;
    case collection?.toLowerCase().includes("hometown"):
      collectionTitle = "Hometown";
      featuresList = collectionFeatures?.value?.hometown ?? [];
      break;
  }

  return {
    title1: collectionTitle + " Features",
    item: featuresList,
  };
});

const popularPlans = computed(() => {
  return {
    backgroundColor: "#f0f0f0",
    items: getPopularPlans(plan?.value, plans?.value),
  };
});

const galleryGrid = computed(() => {
  const galleryItems = Array.isArray(
    plan?.value?.floorplanDetails?.galleryItems,
  )
    ? plan?.value?.floorplanDetails?.galleryItems.map((item) => item.image)
    : [];

  return { gallery: galleryItems, thumbnailCount: 5, backgroundColor: "#fff" };
});

const virtualTour = computed(() => {
  const collection = plan?.value?.floorplanDetails?.collection ?? "";
  const collectionSpace = collection ? collection + " " : "";

  const showButton1 = false;

  return {
    title1: plan?.value?.floorplanDetails?.matterportTourTitle,
    title2: "Tour this " + collectionSpace + " home",
    virtualTourEmbedType: "url",
    virtualTourUrl: plan?.value?.floorplanDetails?.matterportTour,
    button1: showButton1,
    button1Text: "Learn More",
    link1: "#null",
    buttonColors: ["button-gray"],
  };
});

onMounted(() => {
  window.addEventListener(
    "hashchange",
    () => {
      if (window.location.hash === "#modal-form") {
        modalForm.openModal();
        window.location.hash = "";
      }
    },
    false,
  );

  isSmallScreen.value = window.innerWidth <= 767;
  window.addEventListener('resize', () => {
    isSmallScreen.value = window.innerWidth <= 767;
  });

  addTriggers();
});

const addTriggers = () => {
  const modalFormTriggers = document.querySelectorAll(
    "#cta-be-the-first .button-1, #cta-be-the-first a[href='#modal-form']",
  );

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      modalForm.openModal();
    });
  });
};

onUpdated(() => {
  addTriggers();
});

const form = computed(() => {
  return {
    id: "ca2c18ce-092f-4156-aecd-257fb7776d83",
    hiddenFields: [
      {
        inputSelector: 'input[name="floorplan_of_interest"]',
        inputValue: plan?.value?.floorplanDetails?.hubspotValue ?? "",
      },
    ],
  };
});

watch(loading, async (newLoading, oldLoading) => {
  const urlHash = window.location.hash;

  if (urlHash && oldLoading && !newLoading) {
    await nextTick();
    // Check if the element exists before calling scrollIntoView
    if (document.querySelector(urlHash)) {
      document.querySelector(urlHash).scrollIntoView(true);
    }
  }
});
</script>
